/**
 * useMoveLockMutation — bloquejar una reserva a la seva habitació (`plans/reservation-move-lock.md` §4 F).
 *
 * - «Bloquejar» (o canviar el motiu): `PUT /reservations/:id/move-lock` amb `{ reason }`.
 * - «Desbloquejar»: `DELETE /reservations/:id/move-lock` (tanca també l'avís de Beds24).
 * - «Tancar l'avís»: `DELETE /reservations/:id/move-lock/alert` (el bloqueig es queda).
 *
 * En acabar es refresquen el calendari (`signals.moveLock`) i la fitxa; si falla, un toast.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/services/api';

export type MoveLockAction =
  | { kind: 'lock'; reason: string }
  | { kind: 'unlock' }
  | { kind: 'dismiss-alert' };

export interface MoveLockVariables {
  reservationId: string;
  action: MoveLockAction;
}

export interface UseMoveLockMutationOptions {
  onSuccess?: (variables: MoveLockVariables) => void;
}

const ERROR_TEXT: Record<MoveLockAction['kind'], string> = {
  lock: "No s'ha pogut bloquejar la reserva",
  unlock: "No s'ha pogut desbloquejar la reserva",
  'dismiss-alert': "No s'ha pogut tancar l'avís",
};

/** La crida en si, fora del hook perquè es pugui provar amb `api` mockejat. */
export async function sendMoveLock({ reservationId, action }: MoveLockVariables) {
  const url = `/reservations/${encodeURIComponent(reservationId)}/move-lock`;
  switch (action.kind) {
    case 'lock':
      return (await api.put(url, { reason: action.reason.trim() })).data;
    case 'unlock':
      return (await api.delete(url)).data;
    case 'dismiss-alert':
      return (await api.delete(`${url}/alert`)).data;
  }
}

export function useMoveLockMutation(options: UseMoveLockMutationOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendMoveLock,
    onSuccess: (_data: unknown, variables: MoveLockVariables) => {
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['reservation-full', variables.reservationId] });
      options.onSuccess?.(variables);
    },
    onError: (error: unknown, variables: MoveLockVariables) => {
      const detail = error instanceof Error && error.message ? `: ${error.message}` : '';
      toast.error(`${ERROR_TEXT[variables.action.kind]}${detail}`);
    },
  });
}

export default useMoveLockMutation;
